import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ENV } from '@common/app/env';

import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { type GroupFriends } from '@/common/types/groupFriendsType';
import { H1, TextXSmall } from '@/components/Typography/Typography';
import { type RootState } from '@/redux/store';

import Window from '../../RemoveFriendsWindow/Window';

import DropDownList from './DropDownList';

import back from '/img/icons/backMore.svg';
import starSvg from '/img/icons/gradient-star.svg';
import handshake from '/img/icons/handshake.png';
import invite from '/img/icons/invite.svg';
import star from '/img/icons/LevelStar.svg';
import more from '/img/icons/more.svg';
import remove from '/img/icons/removeVector.png';
import top1 from '/img/icons/top1.png';
import top2 from '/img/icons/top2.png';
import top3 from '/img/icons/top3.png';

interface prop {
  setNewGroupWindow: (e: boolean) => void;
}

const YourGroup: React.FC<prop> = ({ setNewGroupWindow }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const groupFriends = useSelector(
    (state: RootState) => state.groupFriends.group as GroupFriends,
  );

  const invitelink = `https://t.me/${ENV.TELEGRAM_BOT_NAME}/game?startapp=${user?.referral}-${groupFriends.uniqueGroupKey}`;
  const shareText = `${t('🔥 Join our community of friends at StarIndustry!')} \n \n ${t('We gather here to play, compete and go through joint events. This is not just an economic game - there are cool mechanics, exciting strategies and the opportunity to develop your industry in the company of friends.')} \n \n ${t('🚀 This is going to be interesting! Join us:')}`;
  const fullurl = `https://t.me/share/url?url=${encodeURIComponent(invitelink)}&text=${encodeURIComponent(shareText)}`;
  const inviteFriends = (): void => {
    Telegram.WebApp.openTelegramLink(fullurl);
  };

  const checkOwner = (): boolean => groupFriends.ownerId === user?.id;

  const [removeParticipantWindow, setRemoveParticipantWindow] = useState(false);
  const [participantId, setParticipantId] = useState('');
  const [dropDownList, setDropDownList] = useState(false);
  const [activeRemoveFriendsMode, setActiveRemoveFriendsMode] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (!(e.target as HTMLElement)?.classList.contains('listMore')) {
        setDropDownList(false);
      }
    };

    document.addEventListener('click', handleClick);

    return (): void => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <>
      <div style={{ position: 'relative', zIndex: '1' }}>
        <div className="yourGroup-main">
          <img src={handshake} alt="handshake" width={77} />
          <H1>{groupFriends.name}</H1>
          <p>
            <span>{groupFriends.participantCount}</span> {t('Friends')}
          </p>
          <div>
            <button
              type="button"
              className="btn"
              style={{
                opacity: dropDownList || activeRemoveFriendsMode ? '0.4' : '1',
                pointerEvents:
                  dropDownList || activeRemoveFriendsMode ? 'none' : 'all',
              }}
              onClick={inviteFriends}
            >
              <img src={invite} alt="invite" width={24} />
              {t('Invite')}
            </button>
            <button
              type="button"
              className="btn btn-dark listMore"
              style={{
                position: 'relative',
                background: dropDownList
                  ? 'linear-gradient(180deg, rgba(153, 153, 153, 0.2) 0%, rgba(255, 255, 255, 0.2) 100%)'
                  : '',
              }}
              onClick={() =>
                activeRemoveFriendsMode
                  ? setActiveRemoveFriendsMode(false)
                  : setDropDownList(!dropDownList)
              }
            >
              <img
                src={activeRemoveFriendsMode ? back : more}
                alt="invite"
                width={20}
              />
              {activeRemoveFriendsMode ? t('Back') : t('More')}
              <DropDownList
                link={invitelink}
                checkOwner={checkOwner}
                dropDownList={dropDownList}
                setDropDownList={setDropDownList}
                setActiveRemoveFriendsMode={setActiveRemoveFriendsMode}
                setNewGroupWindow={setNewGroupWindow}
              />
            </button>
          </div>
        </div>
        <div className="w-full flex items-center py-1 px-2 border border-white/10 rounded-lg gap-1 mt-3">
          <img className="w-4 h-4 shrink-0" src={starSvg} alt="star" />
          <TextXSmall weight="regular" className="!leading-1.2">
            <span className="!text-white/60">
              {t('Star Points values ​​are shown for the')}
            </span>
            <span className="text-white">{` ${t('last')} 20 ${t('days')}`}</span>
          </TextXSmall>
        </div>
        <ul className={`yourGroup-list ${dropDownList ? 'blur' : ''}`}>
          {groupFriends.participants.map((el, index) => {
            let image;

            if (!index) image = top1;
            else if (index === 1) image = top2;
            else if (index === 2) image = top3;

            return (
              <li key={el.id}>
                <div>
                  <img
                    src={el.photoUrl}
                    alt="avatar"
                    width={40}
                    style={{ borderRadius: '50%' }}
                  />
                  <div className="yourGroup-user-info">
                    <div className="yourGroup-user-name">
                      <span className="crop-long-text">
                        {el.firstName} {el.lastName}
                      </span>
                      {image && <img src={image} alt="star" width={16} />}
                    </div>
                    <div className="yourGroup-user-level">
                      <div>
                        {t('Level')} {el.level}
                      </div>
                      {groupFriends.ownerId === el.id && <p>{t('Owner')}</p>}
                    </div>
                  </div>
                </div>
                <div className="yourGroup-user-stars">
                  <p>
                    <img src={star} alt="star" width={convertRemToPixels(1)} />
                    {el.contractPoints}
                  </p>
                  {activeRemoveFriendsMode &&
                    groupFriends.ownerId !== el.id && (
                      <img
                        src={remove}
                        alt="invite"
                        width={24}
                        onClick={() => {
                          setRemoveParticipantWindow(true);
                          setParticipantId(el.id);
                        }}
                      />
                    )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <Window
        participantId={participantId}
        active={removeParticipantWindow}
        setOpen={setRemoveParticipantWindow}
      />
    </>
  );
};

export default YourGroup;
